import {Link, useParams, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {
  ResetPasswordPayload,
  useResetPassword,
} from '../requests/reset-password';
import {useSettings} from '../../core/settings/use-settings';
import {Trans} from '../../i18n/trans';
import {useTrans} from '../../i18n/use-trans';
import {StaticPageTitle} from '../../seo/static-page-title';

export function ResetPasswordPage() {
  const {trans} = useTrans();
  const {token} = useParams();
  const [searchParams] = useSearchParams();
  const searchParamsEmail = searchParams.get('email') || '';
  const {branding} = useSettings();
  const logoSrc = branding?.logo_dark || branding?.logo_light || 'images/logo-dark.png';

  const form = useForm<ResetPasswordPayload>({
    defaultValues: {
      token,
      email: searchParamsEmail,
      password: '',
      password_confirmation: '',
    },
  });
  const resetPassword = useResetPassword(form);

  const rawErrorMessage =
    form.formState.errors.email?.message ||
    form.formState.errors.password?.message ||
    form.formState.errors.password_confirmation?.message ||
    (resetPassword.error as any)?.response?.data?.message ||
    (resetPassword.error as any)?.message;

  const errorMessage = rawErrorMessage
    ? trans({message: rawErrorMessage})
    : null;

  return (
    <div
      className="min-h-screen w-full flex flex-col justify-center items-center relative box-border bg-[#f3f3f3] text-[#1b1b1b]"
      style={{
        backgroundImage:
          "url('https://aadcdn.msauth.net/shared/1.0/content/images/backgrounds/2_bc3d32a696895f78c19df6c717586a5d.svg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        fontFamily: "'Segoe UI', 'Helvetica Neue', 'Lucida Grande', 'Roboto', sans-serif",
      }}
    >
      <StaticPageTitle>
        <Trans message="Đặt lại mật khẩu - TDMU.DEV - URL Shortener" />
      </StaticPageTitle>

      {/* Main Container */}
      <div className="w-full max-w-[440px] p-20 flex flex-col box-border">
        {/* Main Card */}
        <div className="bg-white p-36 sm:p-44 shadow-[0_4px_16px_rgba(0,0,0,0.12)] border border-[#e5e5e5] rounded-[4px] w-full box-border">
          {/* TDMU.DEV Logo & System Name */}
          <div className="mb-8">
            <Link to="/" aria-label="TDMU.DEV" className="inline-block">
              <img
                src={logoSrc}
                className="h-32 max-h-36 w-auto block object-contain"
                alt="TDMU.DEV"
              />
            </Link>
          </div>

          <div className="text-[15px] font-semibold text-[#1b1b1b] mb-24 tracking-[0.5px]">
            TDMU.DEV - URL Shortener
          </div>

          <h1 className="text-[24px] font-semibold text-[#1b1b1b] leading-[28px] mb-12">
            Đặt lại mật khẩu
          </h1>

          <div className="text-[13px] text-[#666] mb-20 leading-normal">
            Nhập thông tin bên dưới để thiết lập mật khẩu mới cho tài khoản của bạn.
          </div>

          {errorMessage && (
            <div className="text-[#e81123] text-[14px] mb-16 leading-tight bg-[#fde7e9] p-10 rounded border border-[#f3b2b8]">
              {errorMessage}
            </div>
          )}

          <form
            onSubmit={form.handleSubmit(payload => {
              resetPassword.mutate(payload);
            })}
          >
            <div className="mb-16">
              <label className="block text-[13px] text-[#1b1b1b] mb-6 font-medium">
                Địa chỉ email
              </label>
              <input
                type="email"
                placeholder="Địa chỉ email"
                autoFocus={!searchParamsEmail}
                className="w-full px-12 py-10 text-[15px] border border-[#8a8886] hover:border-[#323130] focus:border-[#0067b8] focus:ring-1 focus:ring-[#0067b8] outline-none bg-[#fdfdfd] focus:bg-white text-[#1b1b1b] placeholder-[#666] transition-all rounded-[2px] box-border shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)]"
                {...form.register('email', {required: true})}
              />
            </div>

            <div className="mb-16">
              <label className="block text-[13px] text-[#1b1b1b] mb-6 font-medium">
                Mật khẩu mới
              </label>
              <input
                type="password"
                placeholder="Mật khẩu mới"
                autoFocus={!!searchParamsEmail}
                className="w-full px-12 py-10 text-[15px] border border-[#8a8886] hover:border-[#323130] focus:border-[#0067b8] focus:ring-1 focus:ring-[#0067b8] outline-none bg-[#fdfdfd] focus:bg-white text-[#1b1b1b] placeholder-[#666] transition-all rounded-[2px] box-border shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)]"
                {...form.register('password', {required: true})}
              />
            </div>

            <div className="mb-24">
              <label className="block text-[13px] text-[#1b1b1b] mb-6 font-medium">
                Xác nhận mật khẩu mới
              </label>
              <input
                type="password"
                placeholder="Nhập lại mật khẩu mới"
                className="w-full px-12 py-10 text-[15px] border border-[#8a8886] hover:border-[#323130] focus:border-[#0067b8] focus:ring-1 focus:ring-[#0067b8] outline-none bg-[#fdfdfd] focus:bg-white text-[#1b1b1b] placeholder-[#666] transition-all rounded-[2px] box-border shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)]"
                {...form.register('password_confirmation', {required: true})}
              />
            </div>

            <div className="flex items-center justify-between mt-28">
              <Link
                to="/login"
                className="text-[#0067b8] hover:underline text-[13px] font-normal"
              >
                Quay lại đăng nhập
              </Link>

              <button
                type="submit"
                disabled={resetPassword.isPending}
                className="bg-[#0067b8] hover:bg-[#005da6] active:bg-[#004d8c] text-white px-32 py-8 text-[15px] font-normal min-w-[108px] transition-colors cursor-pointer border-none shadow-none disabled:opacity-60 rounded-[2px]"
              >
                {resetPassword.isPending ? 'Đang lưu...' : 'Đặt lại mật khẩu'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
